import { notEmpty } from "@product/scmp-sdk";
import { useMeasure } from "@react-hookz/web";
import { useEffect, useState } from "react";

import { VerticalItemHeight as MoreOnThisItemHeight } from "~/components/article/article-more-on-this-list/consts";
import {
  RightAvailableSpaceTracker,
  WidgetsBottomAvailableSpaceTracker,
} from "~/components/article/article-render/generic-article/styles";
import { useSnapshotState } from "~/lib/hooks";

export const useReduceWhitespaceByAvailableSpace = (
  specs: {
    adSlotSize: number;
    nativeAdSlotSize: number;
    whitespaceAllowance: number;
  },
  isShowInlinePaywall?: boolean,
) => {
  const { adSlotSize, nativeAdSlotSize, whitespaceAllowance } = specs;
  const [enableMeasure, setEnableMeasure] = useState(true);
  const [widgetBottomAvailableSpaceMeasurement, widgetBottomAvailableSpaceMeasurementReference] =
    useMeasure<HTMLDivElement>(enableMeasure);
  const [rightAvailableSpaceMeasurement, rightAvailableSpaceMeasurementReference] =
    useMeasure<HTMLDivElement>(enableMeasure);

  // Prevent CLS of LHS and RHS when content measurement change due to dynamic content
  const [
    widgetBottomAvailableSpaceMeasurementSnapshot,
    setWidgetBottomAvailableSpaceMeasurementSnapshot,
  ] = useSnapshotState(widgetBottomAvailableSpaceMeasurement);
  const [rightAvailableSpaceMeasurementSnapshot, setRightAvailableSpaceMeasurementSnapshot] =
    useSnapshotState(rightAvailableSpaceMeasurement);

  useEffect(() => {
    if (!isShowInlinePaywall) return;
    // Recalculate the space when inline paywall is shown
    setWidgetBottomAvailableSpaceMeasurementSnapshot(
      widgetBottomAvailableSpaceMeasurementReference.current?.getBoundingClientRect(),
    );
    setRightAvailableSpaceMeasurementSnapshot(
      rightAvailableSpaceMeasurementReference.current?.getBoundingClientRect(),
    );
  }, [
    isShowInlinePaywall,
    rightAvailableSpaceMeasurementReference,
    setRightAvailableSpaceMeasurementSnapshot,
    setWidgetBottomAvailableSpaceMeasurementSnapshot,
    widgetBottomAvailableSpaceMeasurementReference,
  ]);

  useEffect(() => {
    // Prevent resize observer keep update the state when we already have the value
    if (
      notEmpty(widgetBottomAvailableSpaceMeasurementSnapshot) &&
      notEmpty(rightAvailableSpaceMeasurementSnapshot)
    )
      setEnableMeasure(false);
  }, [widgetBottomAvailableSpaceMeasurementSnapshot, rightAvailableSpaceMeasurementSnapshot]);

  const availableSpaceAfterTopAds =
    (rightAvailableSpaceMeasurementSnapshot?.height ?? 0) +
    whitespaceAllowance -
    adSlotSize -
    nativeAdSlotSize;
  const availableSpaceAfterMustHaveContent =
    availableSpaceAfterTopAds - MoreOnThisItemHeight * 2 - adSlotSize * 2;

  return {
    reduceWhitespaceByAvailableSpaceSpec: {
      right: {
        shouldRender: rightAvailableSpaceMeasurementSnapshot !== undefined,
        shouldRenderRemaining: availableSpaceAfterMustHaveContent > 0,
        shouldRenderSecondAd: availableSpaceAfterTopAds > 0,
        trackerNode: <RightAvailableSpaceTracker ref={rightAvailableSpaceMeasurementReference} />,
      },
      widgetsBottom: {
        maximumHeight: widgetBottomAvailableSpaceMeasurementSnapshot?.height
          ? widgetBottomAvailableSpaceMeasurementSnapshot.height + whitespaceAllowance
          : undefined,
        trackerNode: (
          <WidgetsBottomAvailableSpaceTracker
            ref={widgetBottomAvailableSpaceMeasurementReference}
          />
        ),
      },
    },
  };
};
